import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import { ControlRoomContext } from '../ControlPage'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Assignment,
  Forum,
  LocalHospital,
  MedicalInformation,
  PeopleAlt,
  Visibility,
} from '@mui/icons-material'
import { Bar, Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip as ChartTooltip,
} from 'chart.js'
import moment from 'moment'

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  ChartTooltip,
  Legend,
  LinearScale,
)

const chartColors = [
  '#239AA4',
  '#E32525',
  '#F28B1F',
  '#00964C',
  '#85276A',
  '#1F3D7C',
  '#CCA43B',
  '#475569',
  '#7C3AED',
  '#0F766E',
]

const CARD_SX = {
  p: 2,
  borderRadius: 3,
  border: '1px solid #E2E8F0',
  backgroundColor: '#FFFFFF',
}

const MetricChip = memo(({ count, title, onClick, icon: Icon }) => (
  <Tooltip title={title}>
    <Stack
      direction="row"
      alignItems="flex-end"
      justifyContent={count ? 'space-between' : 'center'}
      gap={0.75}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 999,
        backgroundColor: 'rgba(35,154,164,0.18)',
        color: '#A7F3F8',
        border: '1px solid rgba(35,154,164,0.32)',
        cursor: 'pointer',
        minWidth: 25,
      }}
      onClick={onClick}
    >
      {Icon && (
        <Box sx={{ display: count ? 'flex' : 'inherit', alignItems: 'center' }}>
          <Icon sx={{ fontSize: 18 }} />
        </Box>
      )}
      {count !== undefined && count !== null && (
        <Typography
          fontWeight={900}
          sx={{ fontSize: '1rem', lineHeight: 1 }}
          color="#A7F3F8"
        >
          {count}
        </Typography>
      )}
    </Stack>
  </Tooltip>
))

const MetricCard = ({ icon: Icon, label, value, helper }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Paper elevation={0} sx={CARD_SX}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="caption" fontWeight={900} color="#64748B">
            {label}
          </Typography>
          {Icon && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                backgroundColor: 'rgba(35,154,164,0.12)',
              }}
            >
              <Icon sx={{ color: '#239AA4', fontSize: 18 }} />
            </Stack>
          )}
        </Stack>
        <Typography variant="h4" fontWeight={950} color="#242F40">
          {value ?? 0}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        )}
      </Stack>
    </Paper>
  </Grid>
)

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    fontWeight={800}
    color="#94A3B8"
    letterSpacing={1}
    sx={{ display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
)

const objectEntries = (object = {}) =>
  Object.entries(object)
    .filter(([, value]) => Number(value) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))

const dateKey = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatDayLabel = (key) =>
  new Date(`${key}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })

const buildDayOptions = ({ linkedEvent, incidents = [], epcrs = [] }) => {
  const keys = new Set()
  const start = linkedEvent?.startDate ? new Date(linkedEvent.startDate) : null
  const end = linkedEvent?.endDate ? new Date(linkedEvent.endDate) : null

  if (
    start &&
    end &&
    !Number.isNaN(start.getTime()) &&
    !Number.isNaN(end.getTime())
  ) {
    const cursor = new Date(start)
    cursor.setHours(12, 0, 0, 0)
    const final = new Date(end)
    final.setHours(12, 0, 0, 0)

    while (cursor <= final) {
      keys.add(dateKey(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
  }

  incidents.forEach((incident) => {
    const key = dateKey(incident.createdAt)
    if (key) keys.add(key)
  })
  epcrs.forEach((epcr) => {
    const key = dateKey(epcr.createdAt)
    if (key) keys.add(key)
  })

  return [...keys].filter(Boolean).sort()
}

// A "smart" stats table: label, count, and a thin proportion-fill bar behind
// the row so relative weight is visible without it being a chart.
const StatTable = ({ title, entries }) => {
  const total = entries.reduce((sum, [, value]) => sum + Number(value || 0), 0)

  return (
    <Paper elevation={0} sx={{ ...CARD_SX }}>
      <Stack spacing={1.5} height="100%">
        <Typography fontWeight={900} color="#242F40">
          {title}
        </Typography>

        {entries.length ? (
          <Stack
            spacing={0.75}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              p: 1,
            }}
          >
            {entries.map(([label, value], i) => {
              const pct = total ? Math.round((Number(value) / total) * 100) : 0
              return (
                <Box
                  key={label}
                  sx={{
                    position: 'relative',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: `${pct}%`,
                      backgroundColor: chartColors[i % chartColors.length],
                      opacity: 0.16,
                      transition: 'width 0.3s ease',
                    }}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ position: 'relative', px: 1.5, py: 1 }}
                  >
                    <Typography variant="body2" fontWeight={600} color="#334155">
                      {label}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography variant="body2" fontWeight={900} color="#242F40">
                        {value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pct}%
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        ) : (
          <Stack flexGrow={1} alignItems="center" justifyContent="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No data recorded yet.
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

const ChartCard = ({ title, type = 'bar', entries }) => {
  const labels = entries.map(([label]) => label)
  const values = entries.map(([, value]) => value)
  const total = values.reduce((sum, value) => sum + Number(value || 0), 0)
  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: labels.map(
          (_, index) => chartColors[index % chartColors.length],
        ),
        borderColor: '#FFFFFF',
        borderWidth: type === 'doughnut' ? 2 : 0,
        borderRadius: type === 'bar' ? 6 : 0,
      },
    ],
  }
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Debounces resize handling so the Dialog's open/scale transition
    // doesn't feed back into repeated ResizeObserver-triggered redraws.
    resizeDelay: 200,
    plugins: {
      legend: {
        display: type === 'doughnut',
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || ''
            const count = Number(context.raw || 0)
            const percentage = total ? Math.round((count / total) * 100) : 0
            return ` ${count} (${percentage}%)`
          },
        },
      },
    },
    scales:
      type === 'bar'
        ? {
            x: {
              ticks: { color: '#475569' },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { precision: 0, color: '#475569' },
              grid: { color: '#E2E8F0' },
            },
          }
        : undefined,
  }

  return (
    <Paper elevation={0} sx={{ ...CARD_SX }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography fontWeight={900} color="#242F40">
            {title}
          </Typography>
          {!!total && (
            <Typography variant="caption" color="text.secondary">
              {total} total
            </Typography>
          )}
        </Stack>
        <Box
          height={340}
          sx={{
            // Hard-clips the canvas so a resize glitch during the Dialog
            // transition can't visually bleed into the row below.
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {entries.length ? (
            type === 'doughnut' ? (
              <Doughnut data={data} options={options} />
            ) : (
              <Bar data={data} options={options} />
            )
          ) : (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <Typography variant="body2" color="text.secondary">
                No data recorded yet.
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  )
}

const Metrics = () => {
  const [openMetrics, setOpenMetrics] = useState(false)
  const [selectedDay, setSelectedDay] = useState('all')
  const {
    metrics,
    resources = [],
    linkedEvent,
    chatUnreadTotal = 0,
    openResourceChat,
  } = useContext(ControlRoomContext)

  const availableResources = resources.filter((r) =>
    ['Available', 'Mobile & Available'].includes(r.status),
  ).length

  const dayOptions = useMemo(
    () =>
      buildDayOptions({
        linkedEvent,
        incidents: metrics?.incidents,
        epcrs: metrics?.epcrs,
      }),
    [linkedEvent, metrics?.epcrs, metrics?.incidents],
  )
  const showDaySelector = dayOptions.length > 1

  const filteredEpcrs = useMemo(() => {
    const nonCancelledEpcrs = (metrics?.epcrs ?? []).filter(
      (epcr) => epcr.incidentStatus !== 'Cancelled',
    )
    if (selectedDay === 'all') return nonCancelledEpcrs
    return nonCancelledEpcrs.filter(
      (epcr) => dateKey(epcr.createdAt) === selectedDay,
    )
  }, [metrics?.epcrs, selectedDay])

  const filteredIncidents = useMemo(() => {
    const nonCancelledIncidents = (metrics?.incidents ?? []).filter(
      (incident) => incident.status !== 'Cancelled',
    )
    if (selectedDay === 'all') return nonCancelledIncidents
    return nonCancelledIncidents.filter(
      (incident) => dateKey(incident.createdAt) === selectedDay,
    )
  }, [metrics?.incidents, selectedDay])

  const reviewStatusCounts = useMemo(
    () =>
      filteredEpcrs.reduce((acc, epcr) => {
        const status = epcr.reviewStatus || 'Unknown'
        acc[status] = (acc[status] || 0) + 1
        return acc
      }, {}),
    [filteredEpcrs],
  )

  const filteredPatientOutcomes = useMemo(
    () =>
      filteredEpcrs.reduce((acc, epcr) => {
        const outcome = epcr.outcome || 'Not recorded'
        acc[outcome] = (acc[outcome] || 0) + 1
        return acc
      }, {}),
    [filteredEpcrs],
  )

  const filteredPresentingComplaints = useMemo(
    () =>
      filteredEpcrs.reduce((acc, epcr) => {
        ;(epcr.presentingComplaints ?? []).forEach((complaint) => {
          if (!complaint) return
          acc[complaint] = (acc[complaint] || 0) + 1
        })
        return acc
      }, {}),
    [filteredEpcrs],
  )

  // Rewritten so each ePCR lands in exactly one bucket: paediatric-flagged
  // records go straight to '<18', records with no usable DOB go to
  // 'Unknown', and everything else is diffed against createdAt once.
  const filteredPatientAgeGroups = useMemo(
    () =>
      filteredEpcrs.reduce((acc, epcr) => {
        const dob = epcr?.demographics?.dob
        const dobUnknown = epcr?.demographics?.dobUnknown
        const dobUnknownButPaediatric =
          epcr?.demographics?.dobUnknownButPaediatric

        // A known DOB wins; the paediatric flag only counts while DOB is unknown
        if (!dob || dobUnknown) {
          if (dobUnknown && dobUnknownButPaediatric) {
            acc['<18'] = (acc['<18'] || 0) + 1
          } else {
            acc['Unknown'] = (acc['Unknown'] || 0) + 1
          }
          return acc
        }

        const age = moment(epcr?.createdAt).diff(moment(dob), 'years')
        if (age < 18) acc['<18'] = (acc['<18'] || 0) + 1
        else if (age <= 25) acc['18-25'] = (acc['18-25'] || 0) + 1
        else if (age <= 40) acc['26-40'] = (acc['26-40'] || 0) + 1
        else if (age <= 60) acc['41-60'] = (acc['41-60'] || 0) + 1
        else if (age <= 80) acc['61-80'] = (acc['61-80'] || 0) + 1
        else acc['80+'] = (acc['80+'] || 0) + 1

        return acc
      }, {}),
    [filteredEpcrs],
  )

  const filteredPatientGenderGroups = useMemo(
    () =>
      filteredEpcrs.reduce((acc, epcr) => {
        const gender = epcr?.demographics?.gender || 'Not recorded'
        acc[String(gender)] = (acc[String(gender)] || 0) + 1
        return acc
      }, {}),
    [filteredEpcrs],
  )

  const patientAgeGroupEntries = useMemo(
    () => objectEntries(filteredPatientAgeGroups),
    [filteredPatientAgeGroups],
  )
  const patientGenderGroupEntries = useMemo(
    () => objectEntries(filteredPatientGenderGroups),
    [filteredPatientGenderGroups],
  )
  const patientOutcomeEntries = useMemo(
    () => objectEntries(filteredPatientOutcomes),
    [filteredPatientOutcomes],
  )
  const presentingComplaintEntries = useMemo(
    () => objectEntries(filteredPresentingComplaints).slice(0, 12),
    [filteredPresentingComplaints],
  )
  const reviewStatusEntries = useMemo(
    () => objectEntries(reviewStatusCounts),
    [reviewStatusCounts],
  )

  const totalIncidentsForPeriod =
    selectedDay === 'all'
      ? (metrics?.totalIncidents ?? filteredIncidents.length)
      : filteredIncidents.length
  const totalPatientsForPeriod =
    selectedDay === 'all'
      ? (metrics?.totalPatientsTreated ?? filteredEpcrs.length)
      : filteredEpcrs.length

  useEffect(() => {
    if (selectedDay !== 'all' && !dayOptions.includes(selectedDay)) {
      setSelectedDay('all')
    }
  }, [dayOptions, selectedDay])

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Tooltip
          title={
            chatUnreadTotal
              ? `${chatUnreadTotal} unread crew message${chatUnreadTotal === 1 ? '' : 's'}`
              : 'Resource messages'
          }
        >
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent={chatUnreadTotal ? 'space-between' : 'center'}
            gap={0.75}
            onClick={() => openResourceChat?.(null)}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 999,
              cursor: 'pointer',
              minWidth: 25,
              backgroundColor: chatUnreadTotal
                ? 'rgba(227,37,37,0.28)'
                : 'rgba(35,154,164,0.18)',
              color: chatUnreadTotal ? '#FECACA' : '#A7F3F8',
              border: chatUnreadTotal
                ? '1px solid rgba(252,165,165,0.55)'
                : '1px solid rgba(35,154,164,0.32)',
              ...(chatUnreadTotal > 0 && {
                animation: 'chatChipPulse 1.3s ease-in-out infinite',
                '@keyframes chatChipPulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(227,37,37,0.45)' },
                  '50%': { boxShadow: '0 0 0 7px rgba(227,37,37,0.08)' },
                },
              }),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Forum sx={{ fontSize: 18 }} />
            </Box>
            {chatUnreadTotal > 0 && (
              <Typography
                fontWeight={900}
                sx={{ fontSize: '1rem', lineHeight: 1 }}
                color="#FECACA"
              >
                {chatUnreadTotal}
              </Typography>
            )}
          </Stack>
        </Tooltip>

        <MetricChip
          count={metrics?.totalIncidents}
          title={`${metrics?.totalIncidents ?? 0} total incidents`}
          onClick={() => setOpenMetrics(true)}
          icon={WarningIcon}
        />
        <MetricChip
          count={metrics?.totalPatientsTreated}
          title={`${metrics?.totalPatientsTreated ?? 0} total patients`}
          onClick={() => setOpenMetrics(true)}
          icon={PeopleAlt}
        />
        <MetricChip
          count={metrics?.totalStaffCurrentlyOnDuty}
          title={`${metrics?.totalStaffCurrentlyOnDuty ?? 0} staff on duty`}
          onClick={() => setOpenMetrics(true)}
          icon={MedicalInformation}
        />
        <MetricChip
          count={availableResources}
          title="Available resources"
          onClick={() => setOpenMetrics(true)}
          icon={LocalHospital}
        />
        <MetricChip
          icon={Assignment}
          title="ePCRs in Draft"
          count={reviewStatusCounts?.['Draft']}
          onClick={() => setOpenMetrics(true)}
        />
        <MetricChip
          title="View all metrics"
          onClick={() => setOpenMetrics(true)}
          icon={Visibility}
        />
      </Stack>

      <Dialog
        open={openMetrics}
        onClose={() => setOpenMetrics(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Stack>
              <Typography fontWeight={950}>Control Metrics</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedDay === 'all' ? 'All days' : formatDayLabel(selectedDay)}
              </Typography>
            </Stack>
            {showDaySelector && (
              <Select
                size="small"
                value={selectedDay}
                onChange={(event) => setSelectedDay(event.target.value)}
                sx={{
                  minWidth: 180,
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  fontWeight: 850,
                }}
              >
                <MenuItem value="all">All days</MenuItem>
                {dayOptions.map((day) => (
                  <MenuItem key={day} value={day}>
                    {formatDayLabel(day)}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: '#F8FAFC' }}>
          <Stack spacing={3} sx={{ py: 2 }}>
            <Box>
              <SectionLabel>Overview</SectionLabel>
              <Grid container spacing={2}>
                <MetricCard
                  icon={WarningIcon}
                  label="Total Incidents"
                  value={totalIncidentsForPeriod}
                  helper={
                    selectedDay === 'all'
                      ? 'Includes closed incidents'
                      : formatDayLabel(selectedDay)
                  }
                />
                <MetricCard
                  icon={PeopleAlt}
                  label="Patients Treated"
                  value={totalPatientsForPeriod}
                  helper={
                    selectedDay === 'all'
                      ? 'ePCRs linked to this event'
                      : formatDayLabel(selectedDay)
                  }
                />
                <MetricCard
                  icon={MedicalInformation}
                  label="Staff On Duty"
                  value={metrics?.totalStaffCurrentlyOnDuty}
                  helper="Unique staff on active resources"
                />
                <MetricCard
                  icon={LocalHospital}
                  label="Available Resources"
                  value={availableResources}
                  helper="Currently marked available"
                />
              </Grid>
            </Box>

            {!!reviewStatusEntries.length && (
              <Box>
                <SectionLabel>ePCR Review Status</SectionLabel>
                <Grid container spacing={2}>
                  {reviewStatusEntries.map(([status, count]) => (
                    <MetricCard
                      key={status}
                      icon={Assignment}
                      label={`${status} ePCRs`}
                      value={count}
                    />
                  ))}
                </Grid>
              </Box>
            )}

            <Box>
              <SectionLabel>Patient Insights</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <ChartCard
                    title="Patient Outcomes"
                    type="doughnut"
                    entries={patientOutcomeEntries}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <ChartCard
                    title="Most Common Presenting Complaints"
                    type="bar"
                    entries={presentingComplaintEntries}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StatTable
                    title="Patient Age Groups"
                    entries={patientAgeGroupEntries}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StatTable
                    title="Patient Genders"
                    entries={patientGenderGroupEntries}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Metrics